import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PIXLIE_SUPPORT_EMAIL } from '../constants/support';
import {
  signInWithEmail,
  resendVerificationEmailWithCredentials,
  signUpWithEmail,
  signInWithGoogle,
  isGoogleAuthAvailable,
} from '../utils/firebase';
import { sanitizeAuthError } from '../utils/userFacingErrors';
import BrightforgeMark from './BrightforgeMark';
import { formatPlanPriceUsd, PUBLIC_PRICING_PLANS } from '../constants/pricingPlans';
import { PixlieDoodle } from './doodles';
import {
  Play,
  Zap,
  Fingerprint,
  Move3d,
  Music,
  Check,
  PlayCircle,
  Download,
  Sliders,
  Smartphone,
  Star,
  Globe,
  ArrowRight,
  ArrowLeft,
  Layers,
  Video,
  X as XIcon,
  Mail,
  Lock,
  Instagram,
  Facebook,
  Cpu,
  Wand2,
  Target,
  ShieldCheck,
  Sun,
  Moon,
} from 'lucide-react';

interface LandingPageProps {
  onLogin: () => void;
  scrollContainerRef?: React.RefObject<HTMLElement>;
  initialFooterPage?: string | null; // For direct URL access: ?page=privacy or ?page=terms
}

const PIXLIE_APP_STORE_URL = 'https://apps.apple.com/app/id6758238801';
const PIXLIE_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.Pixlie.myapp1';
const PIXLIE_PRODUCT_HUNT_URL = 'https://www.producthunt.com/products/pixlie';
const PRODUCT_HUNT_FEATURED_BADGE_SRC =
  'https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1176012&theme=dark&t=1782041607984';
const PRODUCT_HUNT_LOGO_SRC =
  'https://ph-files.imgix.net/6a0057c3-675d-436a-a6cb-30a14f404bb5.svg?auto=compress,format&codec=mozjpeg&cs=strip&fit=crop&h=80&w=80';

const partnerPrograms = [
  {
    name: 'NVIDIA Inception',
    logoSrc: '/partners/nvidia-logo-black.svg',
    logoAlt: 'NVIDIA logo',
    logoClassName: 'h-7 w-auto',
    plateClassName: 'bg-white/[0.92]',
    eyebrow: 'GPU startup program',
    note: 'Startup program and GPU ecosystem.',
    href: 'https://www.nvidia.com/en-us/startups/',
  },
  {
    name: 'Nebius',
    logoSrc: '/partners/nebius-logo.svg',
    logoAlt: 'Nebius logo',
    logoClassName: 'h-6 w-auto',
    plateClassName: 'bg-white/[0.92]',
    eyebrow: 'Cloud partner',
    note: 'Cloud infrastructure and media resources.',
    href: 'https://nebius.com/',
  },
  {
    name: 'YC Startup School',
    logoSrc: '/partners/yc-logo.png',
    logoAlt: 'Y Combinator logo',
    logoClassName: 'h-10 w-auto',
    plateClassName: 'bg-white/[0.94]',
    eyebrow: 'Founder network',
    note: 'Founder program and YC learning network.',
    href: 'https://www.startupschool.org/',
  },
];

const releaseLanes = [
  {
    title: 'iOS',
    eyebrow: 'App Store',
    summary: 'Native Pixlie on iPhone and iPad — same account as pixlys.com, StoreKit subscriptions, and TestFlight updates.',
    chips: ['App Store', 'StoreKit IAP', 'TestFlight'],
    storeUrl: PIXLIE_APP_STORE_URL,
    storeBadgeSrc: '/badges/download-on-the-app-store.svg',
    storeBadgeAlt: 'Download on the App Store',
    storeBadgeClassName: 'h-10 w-auto',
  },
  {
    title: 'Android',
    eyebrow: 'Google Play',
    summary: 'Android build on Google Play — same backend lane as web, rolling out as production geography goes live.',
    chips: ['Google Play', 'Android build', 'Release-ready'],
    storeUrl: PIXLIE_PLAY_STORE_URL,
    storeBadgeSrc: '/badges/google-play-badge.png',
    storeBadgeAlt: 'Get it on Google Play',
    storeBadgeClassName: 'h-14 w-auto',
  },
];

const previewShelf = [
  {
    title: 'Campaign opener',
    creator: '@jd_studio',
    label: 'Cinema cut',
    doodle: 'sparkCut' as const,
  },
  {
    title: 'Look dev pass',
    creator: '@brightforge_lab',
    label: 'Style transfer',
    doodle: 'frames' as const,
  },
  {
    title: 'Music loop test',
    creator: '@cut_room',
    label: 'Audio reactive',
    doodle: 'waveform' as const,
  },
  {
    title: 'Character lock v2',
    creator: '@founder_cut',
    label: 'Identity lock',
    doodle: 'faceLock' as const,
  },
  {
    title: 'Dolly rehearsal',
    creator: '@pixlie_room',
    label: 'Camera move',
    doodle: 'dolly' as const,
  },
  {
    title: 'Lens pick',
    creator: '@look_desk',
    label: 'Optics',
    doodle: 'lens' as const,
  },
  {
    title: 'Continuity pass',
    creator: '@cut_continuity',
    label: 'Thread',
    doodle: 'thread' as const,
  },
  {
    title: 'Prompt to reel',
    creator: '@brief_lab',
    label: 'Text → video',
    doodle: 'promptReel' as const,
  },
];

/** JS marquee — survives prefers-reduced-motion killing CSS animation-duration */
const LandingMarquee: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const cards = [...previewShelf, ...previewShelf];

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    let raf = 0;
    let last = performance.now();
    const speedPxPerSec = 32;

    const loopScroll = () => {
      const half = el.scrollWidth / 2;
      if (half <= 0) return;
      if (el.scrollLeft >= half) el.scrollLeft -= half;
      if (el.scrollLeft < 0) el.scrollLeft += half;
    };

    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!pausedRef.current) {
        el.scrollLeft += speedPxPerSec * dt;
        loopScroll();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onWheel = (e: WheelEvent) => {
      // Vertical wheel → horizontal scrub (trackpads already send deltaX)
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (delta === 0) return;
      e.preventDefault();
      pausedRef.current = true;
      el.scrollLeft += delta;
      loopScroll();
      window.clearTimeout((onWheel as unknown as { _t?: number })._t);
      (onWheel as unknown as { _t?: number })._t = window.setTimeout(() => {
        pausedRef.current = false;
      }, 900);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('wheel', onWheel);
    };
  }, []);

  return (
    <section className="py-14 md:py-16 border-y border-white/5 bg-black/40 backdrop-blur-sm relative">
      <div
        ref={trackRef}
        className="landing-marquee-track flex gap-8 overflow-x-auto px-8 scrollbar-none"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onMouseEnter={() => {
          pausedRef.current = true;
        }}
        onMouseLeave={() => {
          pausedRef.current = false;
        }}
        onPointerDown={() => {
          pausedRef.current = true;
        }}
        onPointerUp={() => {
          pausedRef.current = false;
        }}
      >
        {cards.map((card, i) => (
          <div
            key={`${card.label}-${i}`}
            className="group relative aspect-video w-[280px] sm:w-[300px] md:w-[320px] shrink-0 overflow-hidden rounded-[22px] border border-white/10 bg-[#161616] transition-transform duration-300 hover:scale-[1.015]"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.04] via-transparent to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center pb-10 text-white/55 group-hover:text-white/75 transition-colors">
              <PixlieDoodle
                name={card.doodle}
                size="lg"
                className="opacity-95 drop-shadow-sm"
              />
            </div>
            <div className="absolute inset-x-5 bottom-5">
              <h4 className="text-lg font-semibold text-white/55 transition-colors group-hover:text-white/70">
                {card.label}
              </h4>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
const XSocialIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.901 2H22L15.227 9.742L23.2 22H16.958L12.067 14.574L5.567 22H2.466L9.711 13.724L2.063 2H8.463L12.884 8.786L18.901 2Z" />
  </svg>
);

const BrandWordmark: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <div className="flex items-center gap-3">
    <BrightforgeMark
      className={compact ? 'h-8 w-8 shrink-0' : 'h-9 w-9 shrink-0'}
      tileClassName="bg-white"
      glyphClassName="text-[#111111]"
      outlineClassName="border-white/12 shadow-[0_0_26px_rgba(255,255,255,0.08)]"
    />
    <span className={`font-bold tracking-tighter text-white ${compact ? 'text-lg' : 'text-xl'}`}>PIXLIE</span>
  </div>
);

const SocialIconLink: React.FC<{
  href: string;
  label: string;
  children: React.ReactNode;
}> = ({ href, label, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    className="group flex h-10 w-10 items-center justify-center rounded-full border border-transparent text-white/35 transition-all duration-300 hover:border-white/12 hover:text-white hover:drop-shadow-[0_0_14px_rgba(255,255,255,0.32)]"
  >
    {children}
  </a>
);

/**
 * Scroll Hook: Broadcasts scroll position to CSS variable (--sy)
 */
function useScrollVarPx(scrollContainerRef?: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const el = scrollContainerRef?.current ?? null;
    const target: EventTarget = el ?? window;
    let rafId = 0;

    const update = () => {
      const y = el ? el.scrollTop : window.scrollY;
      document.documentElement.style.setProperty("--sy", `${y}px`);
      rafId = 0;
    };

    const onScroll = () => {
      if (!rafId) rafId = requestAnimationFrame(update);
    };

    update();
    target.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      target.removeEventListener("scroll", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [scrollContainerRef]);
}

/**
 * Reveal Component: Simple scroll animation
 */
const Reveal: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className = "", delay = 0 }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);
                observer.unobserve(node);
            }
        }, { threshold: 0.1 });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            style={{ transitionDelay: `${delay}ms` }}
            className={`transition-all duration-1000 ease-out transform will-change-transform ${isVisible ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-12 blur-sm'} ${className}`}
        >
            {children}
        </div>
    );
};

/**
 * Reusable Footer Component
 */
const Footer: React.FC<{ onNavigate: (page: string) => void }> = ({ onNavigate }) => {
    return (
      <footer className="py-12 px-6 border-t border-white/5 bg-black text-center md:text-left text-sm relative z-10 animate-in fade-in duration-500">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
              <div>
                  <h4 className="font-bold mb-4 text-white uppercase tracking-widest text-xs">Brand</h4>
                  <ul className="space-y-3 text-white/50">
                      <li><button onClick={() => onNavigate('About')} className="hover:text-white transition-colors text-left">About</button></li>
                      <li><button onClick={() => onNavigate('Careers')} className="hover:text-white transition-colors text-left">Careers</button></li>
                      <li><button onClick={() => onNavigate('Blog')} className="hover:text-white transition-colors text-left">Blog</button></li>
                  </ul>
              </div>
              <div>
                  <h4 className="font-bold mb-4 text-white uppercase tracking-widest text-xs">Company</h4>
                  <ul className="space-y-3 text-white/50 text-left text-xs leading-5">
                      <li>
                        <a href="https://brightforge.ltd/" target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors">
                          Brightforge Software Inc.
                        </a>
                        <span className="block text-white/30">Delaware C-Corporation</span>
                        <span className="block text-white/30">8 The Green, STE B #25043</span>
                        <span className="block text-white/30">Dover, DE 19901, USA</span>
                      </li>
                      <li>
                        <span className="block text-white/50">Brightforge Software Oy</span>
                        <span className="block text-white/30">Kaivokselantie 5C, mailbox 66</span>
                        <span className="block text-white/30">01610 Vantaa, Finland</span>
                      </li>
                      <li>
                        <a href={`mailto:${PIXLIE_SUPPORT_EMAIL}?subject=Pixlie%20Support`} className="hover:text-secondary transition-colors">
                          {PIXLIE_SUPPORT_EMAIL}
                        </a>
                        <a href="tel:+19094972155" className="block text-white/30 hover:text-secondary transition-colors">
                          +1 (909) 497-2155
                        </a>
                      </li>
                  </ul>
              </div>
              <div>
                  <h4 className="font-bold mb-4 text-white uppercase tracking-widest text-xs">Support</h4>
                  <ul className="space-y-3 text-white/50">
                      <li>
                        <Link to="/support" className="hover:text-secondary transition-colors">
                          Contact support
                        </Link>
                      </li>
                      <li><button onClick={() => onNavigate('Help Center')} className="hover:text-white transition-colors text-left">Help Center</button></li>
                      <li><a href="/terms/" className="hover:text-white transition-colors">Terms of Service</a></li>
                      <li><a href="/privacy/" className="hover:text-white transition-colors">Privacy Policy</a></li>
                      <li><button onClick={() => onNavigate('Licenses')} className="hover:text-white transition-colors text-left">Licenses & Compliance</button></li>
                  </ul>
              </div>
              <div className="col-span-2">
                  <h4 className="font-bold mb-4 text-white uppercase tracking-widest text-xs">Connect</h4>
                  <div className="flex flex-wrap gap-3 justify-center md:justify-end">
                      <SocialIconLink href="https://www.instagram.com/brightforge_software_inc" label="Instagram">
                          <Instagram className="h-5 w-5" />
                      </SocialIconLink>
                      <SocialIconLink href="https://x.com/brightforge_inc" label="X">
                          <XSocialIcon className="h-[18px] w-[18px]" />
                      </SocialIconLink>
                      <SocialIconLink href="https://www.facebook.com/profile.php?id=61586741459448" label="Facebook">
                          <Facebook className="h-5 w-5" />
                      </SocialIconLink>
                      <SocialIconLink href="https://brightforge.ltd/" label="Brightforge website">
                          <Globe className="h-5 w-5" />
                      </SocialIconLink>
                  </div>
                  <p className="text-white/30 text-xs mt-4">Stay close to the crew.</p>
              </div>
          </div>
          <div className="max-w-7xl mx-auto pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 opacity-40 text-xs">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                  <p>© {new Date().getFullYear()} Brightforge Software Inc. · Brightforge Software Oy</p>
                  <span className="hidden md:inline text-white/20">•</span>
                  <p className="text-white/50">
                      AI powered by commercially licensed models.
                      <button
                          onClick={() => onNavigate('Licenses')}
                          className="underline hover:text-white transition-colors ml-1"
                      >
                          Learn more
                      </button>
                  </p>
              </div>
              <p>Built for teams that actually ship.</p>
          </div>
      </footer>
    );
};

/**
 * SubPage Component (for About, Careers, etc.)
 */
const SubPage: React.FC<{ title: string; onClose: () => void; onNavigate: (page: string) => void }> = ({ title, onClose, onNavigate }) => {
    // Scroll to top when title changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [title]);

    // Render Licenses page
    if (title === 'Licenses') {
        return (
            <div className="min-h-screen bg-black text-white flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300">
                <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/5 px-6 py-4 flex items-center justify-between">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-white/60 hover:text-white hover:text-secondary transition-colors group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                        Back to Home
                    </button>
                    <BrandWordmark compact />
                </nav>

                <main className="flex-1 p-6 mt-20 mb-20 max-w-5xl mx-auto w-full">
                    <div className="text-center mb-12">
                        <span className="text-secondary font-mono text-xs uppercase tracking-[0.2em] mb-4 block">Pixlie Information Protocol</span>
                        <h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-8 text-transparent bg-clip-text bg-gradient-to-b from-white to-white/20">
                            Licenses & Compliance
                        </h1>
                        <div className="w-24 h-1 bg-gradient-to-r from-transparent via-secondary to-transparent mx-auto mb-8"></div>
                        <p className="text-xl text-white/50 leading-relaxed max-w-2xl mx-auto font-light">
                            Our platform uses custom-selected AI models optimized for quality
                            and commercial licensing compatibility in the United States and most countries worldwide.
                        </p>
                    </div>

                    {/* License Overview */}
                    <div className="space-y-6 mb-12">
                        <h2 className="text-2xl font-bold text-white mb-4">Commercial Licensing</h2>

                        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-white/90 mb-3">
                                Full Commercial Rights
                            </h3>
                            <p className="text-sm text-white/70 mb-4">
                                Pixlie provides commercially licensed AI technology for content creation worldwide.
                            </p>
                            <ul className="text-sm text-white/60 space-y-2 list-disc list-inside">
                                <li>Image Generation workflows</li>
                                <li>Video Generation</li>
                                <li>Background Removal</li>
                                <li>Upscaling</li>
                                <li>Character Design</li>
                            </ul>
                        </div>

                        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-white/70 mb-3">
                                Regional Considerations
                            </h3>
                            <p className="text-sm text-white/60">
                                Some AI features may be subject to local regulations in certain jurisdictions.
                                Users are responsible for ensuring compliance with applicable laws in their region.
                            </p>
                        </div>
                    </div>

                    {/* User Responsibility */}
                    <div className="bg-white/5 rounded-lg p-6 border-l-4 border-secondary">
                        <h3 className="text-lg font-semibold text-white mb-3">Your Responsibility</h3>
                        <p className="text-sm text-white/70 mb-4">
                            You are solely responsible for:
                        </p>
                        <ul className="text-sm text-white/60 space-y-2 list-disc list-inside ml-2">
                            <li>Determining compliance with local AI regulations in your jurisdiction</li>
                            <li>Ensuring your use complies with applicable copyright laws</li>
                            <li>Obtaining necessary licenses for restricted components if needed</li>
                        </ul>
                        <p className="text-xs text-white/50 mt-4">
                            <strong>Note:</strong> This information is provided for reference purposes.
                            Consult with a lawyer regarding exact compliance in your jurisdiction.
                        </p>
                    </div>
                </main>

                <Footer onNavigate={onNavigate} />
            </div>
        );
    }

    // Render Privacy Policy page
    if (title === 'Privacy Policy') {
        return (
            <div className="min-h-screen bg-black text-white flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300">
                <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/5 px-6 py-4 flex items-center justify-between">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-white/60 hover:text-white hover:text-secondary transition-colors group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                        Back to Home
                    </button>
                    <BrandWordmark compact />
                </nav>

                <main className="flex-1 p-6 mt-20 mb-20 max-w-4xl mx-auto w-full">
                    <div className="text-center mb-12">
                        <span className="text-secondary font-mono text-xs uppercase tracking-[0.2em] mb-4 block">Legal Information</span>
                        <h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-8 text-transparent bg-clip-text bg-gradient-to-b from-white to-white/20">
                            Privacy Policy
                        </h1>
                        <div className="w-24 h-1 bg-gradient-to-r from-transparent via-secondary to-transparent mx-auto mb-8"></div>
                        <p className="text-sm text-white/50">Last updated: January 21, 2026</p>
                    </div>

                    <div className="space-y-8 text-white/70 leading-relaxed">
                        {/* Introduction */}
                        <div>
                            <p className="text-sm">
                                Brightforge Software Inc. ("Pixlie", "we", "us", or "our") operates the Pixlie application and website.
                                This Privacy Policy explains how we collect, use, disclose, and safeguard your information
                                when you use our Service.
                            </p>
                        </div>

                        {/* Information We Collect */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">1. Information We Collect</h2>

                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">1.1 Account Information</h3>
                                    <p className="text-sm">When you create an account, we collect:</p>
                                    <ul className="text-sm list-disc list-inside ml-4 mt-2 space-y-1">
                                        <li>Email address</li>
                                        <li>Name (if provided)</li>
                                        <li>Profile picture (if provided)</li>
                                        <li>Authentication credentials</li>
                                    </ul>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">1.2 Content You Create</h3>
                                    <p className="text-sm">When you use our AI generation features, we process:</p>
                                    <ul className="text-sm list-disc list-inside ml-4 mt-2 space-y-1">
                                        <li>Text prompts you provide</li>
                                        <li>Images you upload</li>
                                        <li>Videos you upload or generate</li>
                                        <li>Generation settings and preferences</li>
                                    </ul>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">1.3 Usage Data</h3>
                                    <p className="text-sm">We automatically collect:</p>
                                    <ul className="text-sm list-disc list-inside ml-4 mt-2 space-y-1">
                                        <li>Device information (type, OS version)</li>
                                        <li>IP address and approximate location</li>
                                        <li>App usage patterns and feature interactions</li>
                                        <li>Error logs and crash reports</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* How We Use Information */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">2. How We Use Your Information</h2>
                            <p className="text-sm mb-3">We use your information to:</p>
                            <ul className="text-sm list-disc list-inside ml-4 space-y-1">
                                <li>Provide and maintain our Service</li>
                                <li>Process your AI generation requests</li>
                                <li>Send notifications about your content</li>
                                <li>Improve and optimize our Service</li>
                                <li>Respond to your inquiries and support requests</li>
                                <li>Detect and prevent fraud or abuse</li>
                                <li>Comply with legal obligations</li>
                            </ul>
                        </div>

                        {/* Data Sharing */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">3. How We Share Your Information</h2>

                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">3.1 Service Providers</h3>
                                    <p className="text-sm">
                                        We share data with third-party services that help us operate our Service:
                                    </p>
                                    <ul className="text-sm list-disc list-inside ml-4 mt-2 space-y-1">
                                        <li>Cloud computing providers (for AI processing)</li>
                                        <li>Analytics services (for app improvement)</li>
                                        <li>Error tracking services (for stability)</li>
                                        <li>Payment processors (if applicable)</li>
                                    </ul>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">3.2 We Do NOT</h3>
                                    <ul className="text-sm list-disc list-inside ml-4 space-y-1">
                                        <li>Sell your personal information</li>
                                        <li>Share your content publicly without consent</li>
                                        <li>Use your content to train AI models without opt-in</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Data Retention */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">4. Data Retention</h2>
                            <ul className="text-sm list-disc list-inside ml-4 space-y-1">
                                <li>Account data: retained while your account is active</li>
                                <li>Generated content: retained for 30 days after creation, then automatically deleted</li>
                                <li>Usage logs: retained for up to 12 months</li>
                                <li>You can request deletion of your data at any time</li>
                            </ul>
                        </div>

                        {/* Your Rights */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">5. Your Rights</h2>

                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">5.1 All Users</h3>
                                    <p className="text-sm">You have the right to:</p>
                                    <ul className="text-sm list-disc list-inside ml-4 mt-2 space-y-1">
                                        <li>Access your personal data</li>
                                        <li>Correct inaccurate data</li>
                                        <li>Delete your account and data</li>
                                        <li>Export your data</li>
                                        <li>Opt-out of marketing communications</li>
                                    </ul>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">5.2 European Users (GDPR)</h3>
                                    <p className="text-sm">
                                        If you are in the European Economic Area, you have additional rights under GDPR,
                                        including the right to object to processing and data portability.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">5.3 California Users (CCPA)</h3>
                                    <p className="text-sm">
                                        California residents have additional rights under CCPA, including the right
                                        to know what personal information is collected and the right to opt-out of sale
                                        (note: we do not sell personal information).
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Security */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">6. Security</h2>
                            <p className="text-sm">
                                We implement industry-standard security measures to protect your data, including:
                            </p>
                            <ul className="text-sm list-disc list-inside ml-4 mt-2 space-y-1">
                                <li>Encryption in transit (TLS/SSL)</li>
                                <li>Encryption at rest</li>
                                <li>Regular security audits</li>
                                <li>Access controls and authentication</li>
                            </ul>
                        </div>

                        {/* Children */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">7. Children's Privacy</h2>
                            <p className="text-sm">
                                Our Service is not intended for children under 13 years of age (or 16 in the EEA).
                                We do not knowingly collect personal information from children. If you believe we have
                                collected information from a child, please contact us immediately.
                            </p>
                        </div>

                        {/* Changes */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">8. Changes to This Policy</h2>
                            <p className="text-sm">
                                We may update this Privacy Policy from time to time. We will notify you of any changes
                                by posting the new Privacy Policy on this page and updating the "Last updated" date.
                            </p>
                        </div>

                        {/* Contact */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">9. Contact Us</h2>
                            <p className="text-sm">
                                If you have questions about this Privacy Policy or wish to exercise your rights, contact us at:
                            </p>
                            <div className="bg-white/5 rounded-lg p-4 mt-3">
                                <p className="text-sm">
                                    <strong className="text-white">Email:</strong> team@brightforge.ltd<br/>
                                    <strong className="text-white">Company:</strong> Brightforge Software Inc.
                                </p>
                            </div>
                        </div>
                    </div>
                </main>

                <Footer onNavigate={onNavigate} />
            </div>
        );
    }

    // Render Terms of Service page
    if (title === 'Terms of Service') {
        return (
            <div className="min-h-screen bg-black text-white flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300">
                <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/5 px-6 py-4 flex items-center justify-between">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-white/60 hover:text-white hover:text-secondary transition-colors group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                        Back to Home
                    </button>
                    <BrandWordmark compact />
                </nav>

                <main className="flex-1 p-6 mt-20 mb-20 max-w-4xl mx-auto w-full">
                    <div className="text-center mb-12">
                        <span className="text-secondary font-mono text-xs uppercase tracking-[0.2em] mb-4 block">Legal Information</span>
                        <h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-8 text-transparent bg-clip-text bg-gradient-to-b from-white to-white/20">
                            Terms of Service
                        </h1>
                        <div className="w-24 h-1 bg-gradient-to-r from-transparent via-secondary to-transparent mx-auto mb-8"></div>
                        <p className="text-sm text-white/50">Last updated: January 21, 2026</p>
                    </div>

                    <div className="space-y-8 text-white/70 leading-relaxed">
                        {/* Introduction */}
                        <div>
                            <p className="text-sm">
                                Welcome to Pixlie. By accessing or using our Service, you agree to be bound by these Terms of Service.
                                Please read them carefully before using our Service.
                            </p>
                        </div>

                        {/* Acceptance */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
                            <p className="text-sm">
                                By creating an account or using Pixlie, you agree to these Terms of Service and our Privacy Policy.
                                If you do not agree, you may not use our Service.
                            </p>
                        </div>

                        {/* Description of Service */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">2. Description of Service</h2>
                            <p className="text-sm">
                                Pixlie provides AI-powered content generation tools, including but not limited to:
                            </p>
                            <ul className="text-sm list-disc list-inside ml-4 mt-2 space-y-1">
                                <li>Image generation and editing</li>
                                <li>Video generation</li>
                                <li>Character design tools</li>
                                <li>Background removal and upscaling</li>
                            </ul>
                        </div>

                        {/* User Accounts */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">3. User Accounts</h2>
                            <p className="text-sm">You are responsible for:</p>
                            <ul className="text-sm list-disc list-inside ml-4 mt-2 space-y-1">
                                <li>Maintaining the confidentiality of your account</li>
                                <li>All activities that occur under your account</li>
                                <li>Providing accurate and complete information</li>
                                <li>Notifying us of any unauthorized access</li>
                            </ul>
                        </div>

                        {/* Acceptable Use */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">4. Acceptable Use</h2>
                            <p className="text-sm mb-3">You agree NOT to use our Service to:</p>
                            <ul className="text-sm list-disc list-inside ml-4 space-y-1">
                                <li>Generate illegal, harmful, or offensive content</li>
                                <li>Create deepfakes or non-consensual intimate imagery</li>
                                <li>Infringe on intellectual property rights</li>
                                <li>Harass, abuse, or harm others</li>
                                <li>Distribute malware or spam</li>
                                <li>Circumvent security measures</li>
                                <li>Violate any applicable laws</li>
                            </ul>
                        </div>

                        {/* User Content */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">5. User Content</h2>
                            <div className="space-y-3">
                                <p className="text-sm">
                                    <strong className="text-white">5.1 Ownership:</strong> You retain ownership of content you upload.
                                    Generated content is owned by you, subject to any third-party rights.
                                </p>
                                <p className="text-sm">
                                    <strong className="text-white">5.2 License to Us:</strong> You grant us a limited license to process
                                    your content solely to provide the Service.
                                </p>
                                <p className="text-sm">
                                    <strong className="text-white">5.3 Responsibility:</strong> You are solely responsible for ensuring
                                    you have rights to upload content and that generated content complies with applicable laws.
                                </p>
                            </div>
                        </div>

                        {/* Intellectual Property */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">6. Intellectual Property & Licenses</h2>

                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">6.1 Third-Party AI Models</h3>
                                    <p className="text-sm">
                                        Our Service uses third-party AI models licensed under various terms
                                        (Apache 2.0, MIT, and proprietary licenses).
                                        By using the Service, you agree to comply with all applicable license terms.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">6.2 Geographic Restrictions</h3>
                                    <p className="text-sm">
                                        Certain AI features (Text-to-Video and Video-to-Video) are not available
                                        for commercial use in the European Union, United Kingdom, and South Korea
                                        due to licensing limitations.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">6.3 Commercial Use</h3>
                                    <p className="text-sm">
                                        Most features support full commercial use worldwide. See our
                                        <button onClick={() => onNavigate('Licenses')} className="text-secondary hover:underline mx-1">
                                            Licenses & Compliance
                                        </button>
                                        page for details.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Disclaimers */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">7. Disclaimers</h2>
                            <p className="text-sm">
                                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.
                                WE DO NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE,
                                OR THAT GENERATED CONTENT WILL MEET YOUR REQUIREMENTS.
                            </p>
                        </div>

                        {/* Limitation of Liability */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">8. Limitation of Liability</h2>
                            <p className="text-sm">
                                TO THE MAXIMUM EXTENT PERMITTED BY LAW, PIXLIE SHALL NOT BE LIABLE FOR ANY
                                INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR
                                USE OF THE SERVICE.
                            </p>
                        </div>

                        {/* Termination */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">9. Termination</h2>
                            <p className="text-sm">
                                We may terminate or suspend your account at any time for violation of these Terms.
                                You may terminate your account at any time by contacting support.
                            </p>
                        </div>

                        {/* Changes */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">10. Changes to Terms</h2>
                            <p className="text-sm">
                                We may modify these Terms at any time. Continued use of the Service after changes
                                constitutes acceptance of the modified Terms.
                            </p>
                        </div>

                        {/* Governing Law */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">11. Governing Law</h2>
                            <p className="text-sm">
                                These Terms are governed by the laws of the State of Delaware, United States,
                                without regard to conflict of law principles.
                            </p>
                        </div>

                        {/* Contact */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-4">12. Contact</h2>
                            <p className="text-sm">
                                For questions about these Terms, contact us at:
                            </p>
                            <div className="bg-white/5 rounded-lg p-4 mt-3">
                                <p className="text-sm">
                                    <strong className="text-white">Email:</strong> team@brightforge.ltd<br/>
                                    <strong className="text-white">Company:</strong> Brightforge Software Inc.
                                </p>
                            </div>
                        </div>
                    </div>
                </main>

                <Footer onNavigate={onNavigate} />
            </div>
        );
    }

    // Default placeholder for other pages
    return (
        <div className="min-h-screen bg-black text-white flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300">
            {/* Simple Header */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/5 px-6 py-4 flex items-center justify-between">
                <button
                    onClick={onClose}
                    className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-white/60 hover:text-white hover:text-secondary transition-colors group"
                >
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    Back to Home
                </button>
                <BrandWordmark compact />
            </nav>

            {/* Content Placeholder */}
            <main className="flex-1 flex flex-col items-center justify-center p-6 mt-20 mb-20">
                <div className="max-w-3xl text-center">
                    <span className="text-secondary font-mono text-xs uppercase tracking-[0.2em] mb-4 block animate-in fade-in delay-100">Pixlie Information Protocol</span>
                    <h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-8 text-transparent bg-clip-text bg-gradient-to-b from-white to-white/20 animate-in zoom-in-95 duration-500">
                        {title}
                    </h1>
                    <div className="w-24 h-1 bg-gradient-to-r from-transparent via-secondary to-transparent mx-auto mb-12"></div>
                    <p className="text-xl text-white/50 leading-relaxed max-w-2xl mx-auto font-light animate-in fade-in slide-in-from-bottom-4 delay-200">
                        This page represents a placeholder for future content.
                        In a production environment, this would contain the full legal, informational, or blog content for <span className="text-white">{title}</span>.
                    </p>
                </div>
            </main>

            {/* Reused Footer (allows navigation to other sub-pages) */}
            <Footer onNavigate={onNavigate} />
        </div>
    );
};

const LandingPage: React.FC<LandingPageProps> = ({ onLogin, scrollContainerRef, initialFooterPage }) => {
  useScrollVarPx(scrollContainerRef);
  const [navScrolled, setNavScrolled] = useState(false);
  useEffect(() => {
    const el = scrollContainerRef?.current ?? null;
    const target: HTMLElement | Window = el ?? window;
    const readY = () => {
      if (el) return el.scrollTop;
      return window.scrollY || document.documentElement.scrollTop || 0;
    };
    const onScroll = () => setNavScrolled(readY() > 12);
    onScroll();
    target.addEventListener('scroll', onScroll, { passive: true } as AddEventListenerOptions);
    return () => target.removeEventListener('scroll', onScroll as EventListener);
  }, [scrollContainerRef]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  // Support direct URL access via ?page=privacy or ?page=terms
  const [activeFooterPage, setActiveFooterPage] = useState<string | null>(initialFooterPage || null);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [checkEmail, setCheckEmail] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [landingTheme, setLandingTheme] = useState<'night' | 'day'>(() => {
    try {
      const saved = localStorage.getItem('pixlie_landing_theme');
      return saved === 'day' ? 'day' : 'night';
    } catch {
      return 'night';
    }
  });

  const toggleLandingTheme = () => {
    setLandingTheme((prev) => {
      const next = prev === 'night' ? 'day' : 'night';
      try {
        localStorage.setItem('pixlie_landing_theme', next);
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const handleOpenAuth = () => {
    setError(null);
    setSuccess(false);
    setCheckEmail(false);
    setNeedsVerification(false);
    setShowAuthModal(true);
  };

  // Handle authentication
  const handleAuth = async () => {
    setError(null);

    // Validation
    if (!email || !email.trim()) {
      setError('Email is required');
      return;
    }

    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setNeedsVerification(false);

    try {
      if (authMode === 'signup') {
        // Registration через Firebase
        const token = await signUpWithEmail(email.trim(), password);
        localStorage.setItem('firebase_token', token);

        // Backend сам создаст/обновит пользователя в Supabase DB при первом запросе
        setError(null);
        setSuccess(true);

        // Show success message for TestSprite, then navigate
        setTimeout(() => {
          setShowAuthModal(false);
          onLogin();
        }, 1500);
      } else {
        // Login через Firebase
        const token = await signInWithEmail(email.trim(), password);
        localStorage.setItem('firebase_token', token);

        // Backend сам создаст/обновит пользователя в Supabase DB при первом запросе
        setShowAuthModal(false);
        onLogin(); // Call parent callback
      }
    } catch (err: any) {
      const msg = err.message || 'An error occurred';
      if (msg === 'CHECK_EMAIL') {
        setError(null);
        setCheckEmail(true);
        setSuccess(true);
        return;
      }
      if (msg === 'EMAIL_NOT_VERIFIED') {
        setNeedsVerification(true);
        setError(
          'Email not verified. Open the latest inbox link first — signing in again does not send a new email.',
        );
        return;
      }
      setError(sanitizeAuthError(msg));
    } finally {
      setLoading(false);
    }
  };

  // If a footer page is active, show the SubPage instead of the main Landing Page
  if (activeFooterPage) {
      return (
          <SubPage
            title={activeFooterPage}
            onClose={() => setActiveFooterPage(null)}
            onNavigate={setActiveFooterPage}
          />
      );
  }

  return (
    <div
      className="pixlie-landing min-h-screen bg-black text-white font-sans selection:bg-secondary/30 selection:text-white overflow-x-hidden"
      data-landing-theme={landingTheme}
    >

      {/* --- AUTH MODAL --- */}
      {showAuthModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 animate-in fade-in duration-200"
          onClick={(e) => {
            // Закрыть модалку при клике на backdrop
            if (e.target === e.currentTarget) {
              setShowAuthModal(false);
            }
          }}
        >
            <div
              className="w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-3xl p-8 relative overflow-hidden shadow-2xl grain-panel"
              onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button */}
                <button
                    onClick={() => setShowAuthModal(false)}
                    className="absolute top-6 right-6 text-white/40 hover:text-white transition-colors"
                >
                    <XIcon className="w-5 h-5" />
                </button>

                <div className="text-center mb-8">
                    <div className="w-12 h-12 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center mx-auto mb-4 text-secondary animate-pulse-slow">
                        <Fingerprint className="w-6 h-6" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                        {authMode === 'signin' ? 'Welcome Back' : 'Create your account'}
                    </h2>
                    <p className="text-white/40 text-sm">
                        {authMode === 'signin' ? 'Enter the cockpit.' : 'Start directing today.'}
                    </p>
                </div>

                <div className="space-y-4">
                     <div>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2 block pl-1">Email Access</label>
                        <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="director@pixlie.ai"
                                className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white font-mono text-sm focus:border-secondary focus:bg-white/10 outline-none transition-all placeholder:text-white/20"
                                disabled={loading}
                            />
                        </div>
                     </div>
                     <div>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2 block pl-1">Passkey</label>
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white font-mono text-sm focus:border-secondary focus:bg-white/10 outline-none transition-all placeholder:text-white/20"
                                disabled={loading}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !loading) {
                                    handleAuth();
                                  }
                                }}
                            />
                        </div>
                        {authMode === 'signin' && (
                          <p className="mt-2 text-right">
                            <Link
                              to="/forgot-password"
                              onClick={() => setShowAuthModal(false)}
                              className="text-[10px] uppercase tracking-widest text-white/40 hover:text-secondary"
                            >
                              Forgot password?
                            </Link>
                          </p>
                        )}
                     </div>
                     {error && (
                       <div className="text-red-400 text-xs mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded">
                         {error}
                       </div>
                     )}
                     {needsVerification && authMode === 'signin' && (
                       <button
                         type="button"
                         disabled={resending || !email || !password}
                         onClick={async () => {
                           setResending(true);
                           setError(null);
                           try {
                             await resendVerificationEmailWithCredentials(email.trim(), password);
                             setSuccess(true);
                             setCheckEmail(true);
                             setError(null);
                           } catch (err) {
                             setError(
                               sanitizeAuthError(
                                 err instanceof Error ? err.message : 'Could not resend verification.',
                               ),
                             );
                           } finally {
                             setResending(false);
                           }
                         }}
                         className="mt-3 w-full rounded-xl border border-amber-500/30 bg-amber-500/10 py-3 text-[10px] font-bold uppercase tracking-widest text-amber-200"
                       >
                         {resending ? 'Sending…' : 'Resend verification email'}
                       </button>
                     )}
                     {success && (
                       <div className="text-green-400 text-xs mt-2 p-2 bg-green-500/10 border border-green-500/20 rounded" data-testid="registration-success">
                         {checkEmail
                           ? 'Account created. Check your inbox for a verification link.'
                           : 'Account created successfully! Redirecting…'}
                       </div>
                     )}
                </div>

                <button
                    onClick={handleAuth}
                    disabled={loading}
                    className="w-full bg-white text-black font-bold uppercase tracking-widest text-xs py-4 rounded-xl mt-8 hover:bg-secondary hover:scale-[1.02] transition-all duration-300 shadow-[0_0_20px_rgba(255,255,255,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loading ? 'Processing...' : (authMode === 'signin' ? 'Authenticate' : 'Create Account')}
                </button>

                {isGoogleAuthAvailable() && (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={async () => {
                      setLoading(true);
                      setError(null);
                      try {
                        const token = await signInWithGoogle();
                        localStorage.setItem('firebase_token', token);
                        setShowAuthModal(false);
                        onLogin();
                      } catch (err: unknown) {
                        const msg = err instanceof Error ? err.message : 'Google sign-in failed';
                        if (!/redirecting/i.test(msg)) {
                          setError(sanitizeAuthError(msg));
                        }
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="mt-4 w-full rounded-xl bg-[#4285F4] py-4 text-xs font-bold uppercase tracking-widest text-white transition-all hover:bg-[#357AE8] disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    Sign in with Google
                  </button>
                )}

                <div className="mt-6 text-center border-t border-white/5 pt-6">
                     <p className="text-xs text-white/30">
                        {authMode === 'signin' ? "Need an account? " : "Already have an account? "}
                        <button
                            onClick={() => setAuthMode(authMode === 'signin' ? 'signup' : 'signin')}
                            className="text-white hover:text-secondary underline underline-offset-4 decoration-white/20 hover:decoration-secondary transition-all"
                        >
                            {authMode === 'signin' ? 'Create one' : 'Sign In'}
                        </button>
                     </p>
                </div>
            </div>
        </div>
      )}

      {/* --- GLOBAL NAV — transparent at top, matte glass after scroll (design: Webapp Redesign) --- */}
      <nav
        className={[
          'fixed top-0 left-0 right-0 z-50 px-6 py-5 flex items-center justify-between transition-all duration-300',
          navScrolled
            ? 'border-b border-white/[0.08] bg-black/55 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.28)]'
            : 'border-b border-transparent bg-transparent backdrop-blur-0',
        ].join(' ')}
      >
        <div className="group cursor-pointer transition-transform duration-500 hover:scale-[1.02]" onClick={() => window.scrollTo(0,0)}>
            <BrandWordmark />
        </div>

        <div className="flex items-center gap-3 sm:gap-4">
            <button
                type="button"
                aria-label={landingTheme === 'night' ? 'Switch to day mode' : 'Switch to night mode'}
                onClick={toggleLandingTheme}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white/55 hover:text-white hover:border-white/20 transition-colors"
            >
                {landingTheme === 'night' ? (
                  <Sun className="h-4 w-4" strokeWidth={1.5} />
                ) : (
                  <Moon className="h-4 w-4" strokeWidth={1.5} />
                )}
            </button>
            <button
                onClick={() => { setAuthMode('signin'); handleOpenAuth(); }}
                className="hidden sm:block text-sm font-medium text-white/80 hover:text-white transition-colors"
            >
                Sign in
            </button>
            <button
                onClick={() => { setAuthMode('signup'); handleOpenAuth(); }}
                className="bg-white text-black px-5 py-2 rounded-full text-sm font-semibold hover:bg-white/90 transition-colors"
            >
                Sign up
            </button>
        </div>
      </nav>

      {/* --- HERO SECTION --- */}
      <header className="relative min-h-[100svh] flex flex-col items-center justify-center px-6 overflow-hidden">
         {/* Soft night/day wash — no polaroid/viewfinder plates */}
         <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden>
             <div
               className="landing-hero-wash absolute inset-0"
               style={{
                 background:
                   'radial-gradient(120% 90% at 50% 28%, #1c1c20 0%, #111113 42%, #080808 72%, #000000 100%)',
               }}
             />
             <div
               className="landing-hero-sage absolute inset-0 opacity-70"
               style={{
                 background:
                   'radial-gradient(55% 45% at 50% 18%, rgba(134,167,137,0.07) 0%, transparent 60%)',
               }}
             />
             <div
               className="absolute inset-0"
               style={{
                 background:
                   'radial-gradient(80% 60% at 50% 100%, rgba(0,0,0,0.55) 0%, transparent 55%)',
               }}
             />
         </div>

         {/* Hero Content - Fades out on scroll */}
         <div
            className="relative z-10 text-center max-w-5xl mx-auto mt-20 will-change-transform"
            style={{
                transform: 'translateY(calc(var(--sy) * 0.5px))',
                opacity: 'calc(1 - (var(--sy) / 600))'
            }}
         >
            <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-bold tracking-tighter mb-8 leading-[0.92] animate-in fade-in slide-in-from-bottom-8 duration-1000">
               <span className="text-white">Dream It.</span> <br />
               <span className="text-secondary">Render It.</span>
            </h1>

            <p className="text-base sm:text-lg text-white/50 max-w-2xl mx-auto mb-10 font-normal leading-relaxed animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200">
               One room for prompts, camera control, continuity, and final output.
               Enough control to direct the shot without turning the whole process into a mess.
            </p>

            {/* Interactive Mock Input */}
            <div className="max-w-2xl mx-auto relative animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300">
                <div className="relative flex items-center bg-[#121212] border border-white/10 rounded-full p-1.5 pl-5 sm:pl-6">
                    <input
                        type="text"
                        placeholder="Describe the first shot... e.g. 'night exterior, slow dolly'"
                        className="flex-1 min-w-0 bg-transparent border-none outline-none text-white placeholder:text-white/35 text-sm h-11 cursor-pointer"
                        readOnly
                        onClick={handleOpenAuth}
                        aria-label="Click to sign in and create"
                    />
                    <button
                      onClick={handleOpenAuth}
                      className="shrink-0 inline-flex h-11 items-center gap-1.5 rounded-full bg-white px-5 text-sm font-semibold text-black hover:bg-white/90 transition-colors"
                    >
                        Create
                        <ArrowRight className="h-4 w-4" strokeWidth={2} />
                    </button>
                </div>
            </div>
         </div>
      </header>

      <LandingMarquee />

      {/* --- VALUE PROPS --- */}
      <section className="py-20 md:py-24 px-6 max-w-7xl mx-auto">
         <Reveal className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
             <div className="landing-halftone rounded-2xl p-7 md:p-8 flex flex-col min-h-[260px] md:min-h-[280px] group hover:border-white/14 transition-colors">
                 <span className="mb-5 md:mb-6 inline-flex text-secondary">
                   <PixlieDoodle name="promptReel" size="sm" />
                 </span>
                 <h3 className="text-xl font-bold mb-3 text-white">50 Free Credits, Daily</h3>
                 <p className="text-white/45 leading-relaxed text-sm">
                    Turn any moment into customized video instantly. Express what words can't. Free forever, no subscription needed for basic creation.
                 </p>
             </div>

             <div className="landing-halftone rounded-2xl p-7 md:p-8 flex flex-col min-h-[260px] md:min-h-[280px] group hover:border-white/14 transition-colors">
                 <span className="mb-5 md:mb-6 inline-flex text-secondary">
                   <PixlieDoodle name="lens" size="sm" />
                 </span>
                 <h3 className="text-xl font-bold mb-3 text-white">Free AI Video Generator</h3>
                 <p className="text-white/45 leading-relaxed text-sm">
                    Discover what's possible when anyone can direct. Access the market-leading AI engine to explore millions of styles.
                 </p>
             </div>

             <div className="landing-halftone rounded-2xl p-7 md:p-8 flex flex-col min-h-[260px] md:min-h-[280px] group hover:border-white/14 transition-colors">
                 <span className="mb-5 md:mb-6 inline-flex text-secondary">
                   <PixlieDoodle name="thread" size="sm" />
                 </span>
                 <h3 className="text-xl font-bold mb-3 text-white">Share it with the world</h3>
                 <p className="text-white/45 leading-relaxed text-sm">
                    Make video that matters to you, then share it with people who'll feel it too. Viral ready exports.
                 </p>
             </div>
         </Reveal>
      </section>

      {/* --- FEATURE BENTO GRID --- */}
      <section className="px-6 py-24 max-w-7xl mx-auto border-t border-white/5">
          <Reveal className="text-center mb-20">
              <h2 className="text-4xl md:text-6xl font-bold tracking-tighter mb-6 text-white">Built for Directors,<br/>Not Prompt Engineers.</h2>
              <p className="text-white/45 max-w-2xl mx-auto">Most AI tools are slot machines. Pixlie is a cockpit.</p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 grid-rows-2 gap-6 h-auto md:h-[800px]">

              {/* Feature 1: Identity Lock (Large Vertical) */}
              <Reveal className="md:row-span-2 landing-halftone rounded-2xl p-8 flex flex-col justify-between group relative overflow-hidden">
                  <div className="relative z-10">
                      <div className="w-11 h-11 bg-white/[0.04] border border-white/10 rounded-xl flex items-center justify-center mb-6 text-white/80">
                          <Target className="w-5 h-5" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">Identity Lock Protocol</h3>
                      <p className="text-white/45 leading-relaxed text-sm md:text-base">
                          Train a character face once. Use it in a thousand shots. Consistent hair, consistent scars, consistent vibe.
                      </p>
                  </div>

                  <div className="relative z-10 grid grid-cols-2 gap-2 mt-8">
                      {[1,2,3,4].map(i => (
                          <div key={i} className="aspect-square rounded-xl overflow-hidden border border-white/5 group-hover:border-white/15 transition-colors">
                              <img src={`https://picsum.photos/200/200?random=${i+50}`} alt={`Feature showcase image ${i}`} className="w-full h-full object-cover opacity-55 group-hover:opacity-90 transition-opacity grayscale group-hover:grayscale-0" loading="lazy" decoding="async" />
                          </div>
                      ))}
                  </div>
              </Reveal>

              {/* Feature 2: Motion Control (Wide Horizontal) */}
              <Reveal delay={100} className="md:col-span-2 landing-halftone rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between group relative overflow-hidden">
                   <div className="relative z-10 max-w-md">
                        <div className="flex items-center gap-2 mb-4 text-white/70">
                             <div className="w-9 h-9 rounded-lg border border-white/10 bg-white/[0.04] flex items-center justify-center">
                               <Wand2 className="w-4 h-4" strokeWidth={1.5} />
                             </div>
                             <span className="text-xs font-medium uppercase tracking-widest text-white/40">Motion Brush 2.0</span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">Camera Moves, On Purpose.</h3>
                        <p className="text-white/45 text-sm md:text-base">
                            Set pan, tilt, zoom, or orbit deliberately. Draw the path and keep the model inside a move you can actually use.
                        </p>
                   </div>
                   <div className="relative z-10 mt-8 md:mt-0">
                        <div className="w-64 h-40 border border-white/12 rounded-xl bg-black/40 relative overflow-hidden">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-px bg-white/15"></div>
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-full bg-white/15"></div>
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 border border-secondary/40 rounded-full animate-ping opacity-20"></div>
                        </div>
                   </div>
              </Reveal>

              {/* Feature 3: Style Transfer */}
              <Reveal delay={200} className="landing-halftone rounded-2xl p-8 group hover:border-white/14 transition-colors">
                  <div className="w-9 h-9 rounded-lg border border-white/10 bg-white/[0.04] flex items-center justify-center mb-6 text-white/80">
                    <Move3d className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-white">Video-to-Video</h3>
                  <p className="text-sm text-white/45">Restyle source footage without throwing away timing, blocking, or the good parts of the original take.</p>
              </Reveal>

              {/* Feature 4: Audio Reactive */}
              <Reveal delay={300} className="landing-halftone rounded-2xl p-8 group hover:border-white/14 transition-colors">
                  <div className="w-9 h-9 rounded-lg border border-white/10 bg-white/[0.04] flex items-center justify-center mb-6 text-white/80">
                    <Music className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-white">Audio Reactive</h3>
                  <p className="text-sm text-white/45">Shape motion and intensity around the track instead of faking sync later in the edit.</p>
              </Reveal>

          </div>
      </section>

      {/* --- COMMUNITY FEED (Vertical Video Grid) --- */}
      <section className="py-20 md:py-24 bg-black relative">
          <div className="max-w-7xl mx-auto px-6 mb-10 md:mb-14 text-center">
              <Reveal>
                  <h2 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter mb-3 md:mb-4 leading-[1.05]">
                    Study real passes,<br/>not stock demos
                  </h2>
                  <p className="text-white/50 text-sm md:text-base max-w-xl mx-auto">
                    Reference cuts, look-dev tests, and outputs worth stealing ideas from.
                  </p>
              </Reveal>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 px-4 sm:px-6 max-w-7xl mx-auto pb-4 md:pb-8">
              {[1, 2, 3, 4].map((i) => (
                  <Reveal key={i} delay={i * 100} className="aspect-[9/16] rounded-2xl overflow-hidden relative group cursor-pointer border border-white/10 hover:border-secondary/50 transition-all">
                      <img src={`https://picsum.photos/400/700?random=${i+20}`} alt={`Gallery video thumbnail ${i}: Pixlie test cut`} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" loading="lazy" decoding="async" />
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80"></div>
                      <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                          <PlayCircle className="w-7 h-7 sm:w-8 sm:h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="rgba(255,255,255,0.2)" />
                      </div>
                      <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4">
                          <p className="text-sm font-bold text-white mb-0.5">Test cut 0{i}</p>
                          <p className="text-xs text-white/50">@pixlie_session</p>
                      </div>
                  </Reveal>
              ))}
          </div>
      </section>

       {/* --- NATIVE LANES SECTION --- */}
       <section className="px-6 py-24">
          <div className="mx-auto max-w-6xl border-t border-white/10 pt-10">
              <Reveal className="grid gap-6 lg:grid-cols-[0.78fr_minmax(0,1fr)] lg:items-start">
                  <div>
                      <p className="text-[10px] uppercase tracking-[0.28em] text-secondary">Native rollout</p>
                      <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white">Mobile release lanes, without the fake chest-beating.</h2>
                      <p className="mt-3 max-w-md text-sm leading-6 text-white/48">
                          The boring but necessary part: App Store Connect, TestFlight, Play Console, and an actual route to shipping native apps.
                      </p>
                      <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/8 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-emerald-200/80">
                          <ShieldCheck className="h-3.5 w-3.5" />
                          Distribution lanes only
                      </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                      {releaseLanes.map((lane, index) => (
                          <Reveal key={lane.title} delay={80 + index * 90}>
                              <div className="group rounded-[26px] border border-white/10 bg-white/[0.03] p-5 transition-all hover:border-white/20 hover:bg-white/[0.05]">
                                  <p className="text-[10px] uppercase tracking-[0.28em] text-white/30">{lane.eyebrow}</p>
                                  <div className="mt-4 flex min-h-[84px] flex-col justify-center gap-4 rounded-[20px] border border-white/8 bg-black/30 px-5 py-5 sm:flex-row sm:items-center">
                                      <div>
                                          <p className="text-lg font-semibold text-white">{lane.title}</p>
                                          <p className="mt-1 text-sm text-white/46">Official store listing</p>
                                      </div>
                                      <a
                                          href={lane.storeUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex shrink-0 transition-opacity hover:opacity-85"
                                          aria-label={lane.storeBadgeAlt}
                                      >
                                          <img
                                              src={lane.storeBadgeSrc}
                                              alt={lane.storeBadgeAlt}
                                              className={lane.storeBadgeClassName}
                                              loading="lazy"
                                          />
                                      </a>
                                  </div>
                                  <p className="mt-4 text-sm leading-6 text-white/50">{lane.summary}</p>
                                  <div className="mt-4 flex flex-wrap gap-2">
                                      {lane.chips.map(chip => (
                                          <span
                                              key={chip}
                                              className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-white/55"
                                          >
                                              {chip}
                                          </span>
                                      ))}
                                  </div>
                              </div>
                          </Reveal>
                      ))}
                  </div>
              </Reveal>
          </div>
      </section>

      {/* --- PRICING TABLE --- */}
      <section id="pricing" className="px-6 py-24">
         <div className="mx-auto max-w-7xl">
           <Reveal className="mb-16 grid gap-6 border-t border-white/10 pt-10 lg:grid-cols-[0.8fr_minmax(0,1fr)] lg:items-end">
             <div>
               <p className="text-[10px] uppercase tracking-[0.32em] text-secondary/70">Pricing</p>
               <h2 className="mt-4 text-5xl font-bold tracking-tighter text-white">Pick the lane, not the sticker.</h2>
             </div>
               <p className="max-w-2xl text-sm leading-7 text-white/52 lg:justify-self-end">
               Same plans as Billing in your account: Free, Basic, Pro, and Enterprise. Limits and prices match checkout.
             </p>
           </Reveal>

           <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
             {PUBLIC_PRICING_PLANS.map((plan, index) => (
               <Reveal
                 key={plan.id}
                 delay={index * 100}
                 className="landing-halftone flex h-full flex-col rounded-2xl border border-white/10 bg-[#161616] p-7 lg:p-8"
               >
                 <div className="flex items-start justify-between gap-4">
                   <div>
                     <p className="text-[10px] uppercase tracking-[0.28em] text-white/35">{plan.laneLabel}</p>
                     <h3 className="mt-3 text-2xl font-bold tracking-tight text-white">{plan.displayName}</h3>
                   </div>
                   {plan.accent ? (
                     <span className="landing-best-value rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]">
                       Best value
                     </span>
                   ) : null}
                 </div>

                 <div className="mt-8 flex items-end gap-2">
                   <span className="text-4xl font-bold tracking-tight text-white">{formatPlanPriceUsd(plan.priceMonthlyUsd)}</span>
                   <span className="pb-1 text-sm text-white/40">/mo</span>
                 </div>

                 <p className="mt-4 min-h-[84px] text-sm leading-7 text-white/45">{plan.description}</p>

                 <button
                   onClick={() => { setAuthMode('signup'); handleOpenAuth(); }}
                   className={`mt-8 inline-flex items-center justify-center rounded-full px-4 py-3.5 text-sm font-semibold transition-colors ${
                     plan.accent
                       ? 'bg-white text-black hover:bg-white/90'
                       : 'border border-white/15 text-white hover:bg-white hover:text-black'
                   }`}
                 >
                   {plan.cta}
                 </button>

                 <div className="mt-8 space-y-3 border-t border-white/[0.08] pt-6">
                   {plan.features.map((feature) => (
                     <div key={feature} className="flex items-start gap-3 text-sm text-white/55">
                       <Check className="landing-check mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
                       <span>{feature}</span>
                     </div>
                   ))}
                 </div>
               </Reveal>
             ))}
           </div>

        </div>
      </section>

      <section className="px-6 pb-12">
          <div className="mx-auto max-w-6xl border-t border-white/10 pt-10">
              <Reveal className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:items-start">
                  <div>
                      <h2 className="text-2xl font-semibold tracking-tight text-white">Our partners</h2>
                      <p className="mt-3 text-sm leading-6 text-white/48">
                          Official doors only. No hero badge wall, no invented trust theater.
                      </p>
                  </div>

                  <div className="space-y-8">
                      <div className="grid gap-3 md:grid-cols-3">
                          {partnerPrograms.map(program => (
                              <a
                                  key={program.name}
                                  href={program.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group rounded-[26px] border border-white/10 bg-white/[0.03] p-5 transition-all hover:border-white/20 hover:bg-white/[0.05]"
                              >
                                  <p className="text-[10px] uppercase tracking-[0.28em] text-white/30">{program.eyebrow}</p>
                                  <div className={`mt-4 flex min-h-[84px] items-center justify-center rounded-[20px] border border-black/5 px-4 py-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] transition-transform duration-200 group-hover:-translate-y-0.5 ${program.plateClassName}`}>
                                      <img
                                          src={program.logoSrc}
                                          alt={program.logoAlt}
                                          className={program.logoClassName}
                                          loading="lazy"
                                      />
                                  </div>
                                  <p className="mt-4 text-lg font-semibold text-white">{program.name}</p>
                                  <p className="mt-2 text-sm leading-6 text-white/50">{program.note}</p>
                                  <div className="mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-secondary">
                                      Visit official page
                                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                                  </div>
                              </a>
                          ))}
                      </div>

                      <div className="rounded-[26px] border border-white/10 bg-white/[0.02] p-5 md:p-6">
                          <p className="text-[10px] uppercase tracking-[0.28em] text-white/30">Product Hunt</p>
                          <p className="mt-2 text-sm leading-6 text-white/48">
                              Launch proof from June 2026 — #9 Product of the Day plus featured placement.
                          </p>
                          <div className="mt-5 flex flex-col gap-4 xl:flex-row xl:flex-wrap xl:items-center">
                              <a
                                  href={`${PIXLIE_PRODUCT_HUNT_URL}?embed=true&utm_source=embed&utm_medium=post_embed`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group block max-w-sm rounded-xl border border-white/12 bg-white/[0.04] p-4 shadow-[0_2px_12px_rgba(0,0,0,0.35)] transition-colors hover:border-white/20 hover:bg-white/[0.06]"
                              >
                                  <div className="flex items-start gap-3">
                                      <img
                                          src={PRODUCT_HUNT_LOGO_SRC}
                                          alt="Pixlie on Product Hunt"
                                          className="h-12 w-12 rounded-full border border-white/10 bg-white"
                                          loading="lazy"
                                      />
                                      <div>
                                          <p className="text-base font-semibold text-white">Pixlie</p>
                                          <p className="mt-1 text-sm leading-5 text-white/55">
                                              AI video studio: text &amp; image to video, with real control
                                          </p>
                                      </div>
                                  </div>
                                  <span className="mt-4 inline-flex w-full items-center justify-center rounded-lg bg-[#ff6154] px-4 py-2.5 text-sm font-semibold text-white transition-colors group-hover:bg-[#ff4f41]">
                                      Check it out on Product Hunt →
                                  </span>
                              </a>

                              <a
                                  href={`${PIXLIE_PRODUCT_HUNT_URL}?embed=true&utm_source=badge-top9&utm_medium=badge&utm_campaign=badge-pixlie`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex transition-opacity hover:opacity-90"
                              >
                                  <img
                                      src="/assets/ph/pixlie-top9-day-badge-dark.svg"
                                      width={250}
                                      height={54}
                                      alt="#9 Product of the Day on Product Hunt"
                                      loading="lazy"
                                  />
                              </a>

                              <a
                                  href={`${PIXLIE_PRODUCT_HUNT_URL}?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-pixlie`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex transition-opacity hover:opacity-90"
                              >
                                  <img
                                      src={PRODUCT_HUNT_FEATURED_BADGE_SRC}
                                      width={250}
                                      height={54}
                                      alt="Pixlie — Featured on Product Hunt"
                                      loading="lazy"
                                  />
                              </a>
                          </div>
                      </div>
                  </div>
              </Reveal>
          </div>
      </section>

      {/* --- REUSABLE FOOTER --- */}
      <Footer onNavigate={setActiveFooterPage} />

    </div>
  );
};

// Simple Icon Wrappers
function Apple(props: any) {
    return <svg {...props} viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.74 1.18 0 2.45-1.62 4.37-1.34 1.1.2 2.37.57 3.08 1.48-2.76 1.57-2.31 5.95.42 7.15-.51 1.63-1.4 3.24-2.95 4.94zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
}

function X(props: any) { // X / Twitter
    return <svg {...props} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
}

export default LandingPage;
